// Library
import React, { useId, useMemo, useState } from 'react';
import { Text } from '@components';
import { Time } from '@internationalized/date';
import { WarningQld } from '@public/icons';
import {
	TimeField as AriaTimeField,
	TimeFieldProps as AriaTimeFieldProps,
	DateInput,
	DateSegment,
} from 'react-aria-components';

import { FieldWidth } from '..';

// CSS Style for this module
import styles from './TimeField.module.scss';

/**
 * Interface for TimeFieldProps props
 */
interface TimeFieldProps {
	/**
	 * Custom styles of the field
	 */
	customStyles?: string;
	/**
	 * Default value for edit mode
	 */
	defaultValue?: Time;
	/**
	 * Granularity of the time field
	 */
	granularity?: AriaTimeFieldProps<Time>['granularity'];
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (value: Time | null) => void;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Width size of the input element.
	 * Accepts 'small', 'medium', 'large' or 'full' as fluid width.
	 * Or 'fixedWidth1' to 'fixedWidth6' for a standardized fixed width.
	 */
	size?: FieldWidth;
	/** Element identifier selector for test */
	testId?: string;
}

export function TimeField({
	customStyles,
	defaultValue,
	granularity = 'minute',
	handleChange = () => {
		return;
	},
	hintText,
	errorMsg,
	disabled,
	size = 'full',
	testId,
}: TimeFieldProps) {
	const internalId = useId();
	const [hintId] = useState(`${internalId}-hint`);
	const [errorId] = useState(`${internalId}-error`);
	const [time, setTime] = useState<Time | null>(defaultValue ?? null);
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);

	const onChangeValue = (value: Time | null) => {
		setTime(value);
		handleChange(value);
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			<div className={styles.textContainer}>
				{hintText && (
					<div>
						<Text variant="muted" id={hintId}>
							{hintText}
						</Text>
					</div>
				)}
				{hasError && (
					<div className={styles.errorContainer}>
						<Text variant="error" id={errorId} role="alert" className={styles.errorContent}>
							<WarningQld customStyles={styles.errorIcon} /> {errorMsg}
						</Text>
					</div>
				)}
			</div>
			<AriaTimeField
				hourCycle={24}
				value={time}
				granularity={granularity}
				onChange={onChangeValue}
				shouldForceLeadingZeros
				className={styles.timeField}
				isDisabled={disabled}
				data-testid={testId}
			>
				<DateInput
					className={[
						styles.dateInput,
						styles[`field-width--${size}`],
						hasError && styles.error,
					].join(' ')}
				>
					{segment => (
						<DateSegment segment={segment} data-testid={testId} className={styles.dateSegment}>
							{({ text, isPlaceholder }) => (
								<>
									{isPlaceholder
										? segment.type === 'hour'
											? 'hh'
											: segment.type === 'minute'
												? 'mm'
												: 'ss'
										: text}
								</>
							)}
						</DateSegment>
					)}
				</DateInput>
			</AriaTimeField>
		</div>
	);
}
